/**
 * Tabs layout — bottom tab navigator for main app screens.
 */

import React, { useCallback, useEffect, useMemo } from 'react';
import { Tabs, useRouter, useSegments } from 'expo-router';
import { AppState, Keyboard, Platform, Pressable, StyleSheet, Text, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Ionicons } from '@expo/vector-icons';
import { DateTime } from 'luxon';
import { Gesture, GestureDetector } from 'react-native-gesture-handler';
import { runOnJS } from 'react-native-reanimated';
import { useTheme, type ThemeColors } from '../../src/utils/theme';
import { useCalendarStore } from '../../src/stores/calendarStore';
import { useRemindersStore } from '../../src/stores/reminderStore';
import { getReminderStatus } from '../../src/types/api';
import { useNavigationStore } from '../../src/stores/navigationStore';
import { useNotificationHistory } from '../../src/stores/notificationHistoryStore';
import SupportBanner from '../../src/components/support/SupportBanner';
import GlobalDayNavigatorOverlay from '../../src/components/calendar/GlobalDayNavigatorOverlay';

type TabKey = 'chat' | 'reminders' | 'calendar' | 'settings';
type TabIcon = React.ComponentProps<typeof Ionicons>['name'];

type TabItem = {
  key: TabKey;
  label: string;
  route: string;
  icon: TabIcon;
  activeIcon?: TabIcon;
  badge?: number;
  badgeColor?: string;
};

const TAB_KEYS: TabKey[] = ['chat', 'reminders', 'calendar', 'settings'];
const MIN_BOTTOM_KEYBOARD_HEIGHT = 80;

function getActiveTab(segments: string[]): TabKey {
  const activeSegment = [...segments].reverse().find((segment): segment is TabKey => TAB_KEYS.includes(segment as TabKey));
  return activeSegment ?? 'chat';
}

function formatBadgeCount(count?: number): string | null {
  if (!count || count <= 0) return null;
  return count > 99 ? '99+' : String(count);
}

function createTabBarStyles(colors: ThemeColors, isDark: boolean) {
  const navBackground = isDark ? '#10253C' : '#F6FAFD';
  const navHandle = isDark ? 'rgba(120, 159, 203, 0.36)' : 'rgba(94, 129, 164, 0.24)';
  const currentBg = isDark ? 'rgba(66, 165, 245, 0.16)' : 'rgba(66, 165, 245, 0.10)';
  const currentBorder = isDark ? 'rgba(102, 181, 255, 0.32)' : 'rgba(66, 165, 245, 0.22)';
  const currentText = isDark ? '#B6DAFF' : '#2A69A4';
  const shadowColor = isDark ? '#020A12' : '#0C1B2D';

  return StyleSheet.create({
    container: {
      flex: 1,
    },
    body: {
      flex: 1,
    },
    collapsedNav: {
      position: 'absolute',
      left: 0,
      right: 0,
      bottom: 0,
      minHeight: 34,
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 8,
      paddingHorizontal: 10,
      backgroundColor: navBackground,
      shadowColor,
      shadowOpacity: isDark ? 0.2 : 0.08,
      shadowRadius: 10,
      shadowOffset: { width: 0, height: -4 },
      elevation: 8,
      zIndex: 40,
    },
    collapsedNavOpener: {
      minHeight: 28,
      paddingHorizontal: 6,
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 7,
    },
    collapsedCurrentSlot: {
      position: 'absolute',
      top: 0,
      right: 10,
      bottom: 0,
      justifyContent: 'center',
    },
    collapsedCurrentPill: {
      minHeight: 26,
      paddingHorizontal: 10,
      borderRadius: 999,
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 4,
      backgroundColor: currentBg,
      borderWidth: StyleSheet.hairlineWidth,
      borderColor: currentBorder,
    },
    collapsedCurrentText: {
      color: currentText,
      fontSize: 10,
      lineHeight: 12,
      fontFamily: 'Inter_700Bold',
    },
    collapsedHandle: {
      width: 44,
      height: 5,
      borderRadius: 999,
      backgroundColor: navHandle,
    },
    expandedNav: {
      position: 'absolute',
      left: 0,
      right: 0,
      bottom: 0,
      minHeight: 82,
      backgroundColor: navBackground,
      shadowColor,
      shadowOpacity: isDark ? 0.22 : 0.08,
      shadowRadius: 12,
      shadowOffset: { width: 0, height: -4 },
      elevation: 10,
      zIndex: 40,
    },
    navHandleButton: {
      height: 18,
      alignItems: 'center',
      justifyContent: 'center',
    },
    navHandle: {
      width: 42,
      height: 4,
      borderRadius: 999,
      backgroundColor: navHandle,
    },
    itemsRow: {
      minHeight: 58,
      flexDirection: 'row',
      alignItems: 'center',
    },
    item: {
      flex: 1,
      alignItems: 'center',
      justifyContent: 'center',
      gap: 2,
      paddingVertical: 4,
    },
    itemIconWrap: {
      minWidth: 24,
      alignItems: 'center',
      justifyContent: 'center',
    },
    label: {
      fontSize: 11,
      lineHeight: 14,
      fontFamily: 'Inter_500Medium',
    },
    badge: {
      position: 'absolute',
      top: -6,
      right: -10,
      minWidth: 18,
      height: 18,
      borderRadius: 9,
      paddingHorizontal: 5,
      alignItems: 'center',
      justifyContent: 'center',
    },
    badgeText: {
      color: '#FFFFFF',
      fontSize: 10,
      lineHeight: 11,
      fontFamily: 'Inter_700Bold',
    },
    collapsedBadge: {
      minWidth: 16,
      height: 16,
      borderRadius: 8,
      paddingHorizontal: 4,
      alignItems: 'center',
      justifyContent: 'center',
      marginLeft: 2,
    },
    collapsedBadgeText: {
      color: '#FFFFFF',
      fontSize: 9,
      lineHeight: 10,
      fontFamily: 'Inter_700Bold',
    },
  });
}

function CollapsibleTabBar({
  activeTab,
  items,
  colors,
  isDark,
  insetsBottom,
  navOpen,
  dayNavigatorOpen,
  onNavigate,
  onOpenNav,
  onCloseNav,
  onCalendarToday,
  onOpenCalendarDayPanel,
  showNavigationLabel,
  hideNavigationLabel,
  todayLabel,
}: {
  activeTab: TabKey;
  items: TabItem[];
  colors: ThemeColors;
  isDark: boolean;
  insetsBottom: number;
  navOpen: boolean;
  dayNavigatorOpen: boolean;
  onNavigate: (route: string) => void;
  onOpenNav: () => void;
  onCloseNav: () => void;
  onCalendarToday: () => void;
  onOpenCalendarDayPanel: () => void;
  showNavigationLabel: string;
  hideNavigationLabel: string;
  todayLabel: string;
}) {
  const styles = useMemo(() => createTabBarStyles(colors, isDark), [colors, isDark]);
  const collapsedGesture = useMemo(
    () => Gesture.Pan()
      .activeOffsetY([-8, 8])
      .failOffsetX([-20, 20])
      .onEnd((event) => {
        if (event.translationY >= -8 && event.velocityY >= -200) return;
        runOnJS(onOpenCalendarDayPanel)();
      }),
    [onOpenCalendarDayPanel],
  );
  const expandedGesture = useMemo(
    () => Gesture.Pan()
      .activeOffsetY([-8, 8])
      .failOffsetX([-20, 20])
      .onEnd((event) => {
        if (event.translationY < -8 || event.velocityY < -200) {
          runOnJS(onOpenCalendarDayPanel)();
          return;
        }
        if (event.translationY > 8 || event.velocityY > 200) runOnJS(onCloseNav)();
      }),
    [onCloseNav, onOpenCalendarDayPanel],
  );

  const activeItem = items.find((item) => item.key === activeTab) ?? items[0];
  const collapsedBadge = formatBadgeCount(activeItem.badge);
  const showTodayShortcut = activeTab === 'calendar' || dayNavigatorOpen;
  const showCollapsedCurrentSlot = showTodayShortcut || (activeTab !== 'chat' && activeTab !== 'reminders');
  const collapsedLabel = activeTab === 'chat' || activeTab === 'reminders' ? null : activeItem.label;

  if (navOpen) {
    return (
      <GestureDetector gesture={expandedGesture}>
        <View style={[styles.expandedNav, { paddingBottom: insetsBottom > 0 ? insetsBottom : 8 }]}>
          <Pressable onPress={onCloseNav} style={styles.navHandleButton} accessibilityRole="button" accessibilityLabel={hideNavigationLabel}>
            <View style={styles.navHandle} />
          </Pressable>
          <View style={styles.itemsRow}>
            {items.map((item) => {
              const isActive = item.key === activeTab;
              const color = isActive ? colors.tabBarActive : colors.tabBarInactive;
              const badgeLabel = formatBadgeCount(item.badge);

              return (
                <Pressable
                  key={item.key}
                  onPress={() => onNavigate(item.route)}
                  style={styles.item}
                  accessibilityRole="button"
                  accessibilityLabel={item.label}
                  accessibilityState={{ selected: isActive }}
                >
                  <View style={styles.itemIconWrap}>
                    <Ionicons name={isActive ? item.activeIcon ?? item.icon : item.icon} size={22} color={color} />
                    {badgeLabel ? (
                      <View style={[styles.badge, { backgroundColor: item.badgeColor ?? colors.primary }]}>
                        <Text style={styles.badgeText}>{badgeLabel}</Text>
                      </View>
                    ) : null}
                  </View>
                  <Text style={[styles.label, { color }]} numberOfLines={1}>{item.label}</Text>
                </Pressable>
              );
            })}
          </View>
        </View>
      </GestureDetector>
    );
  }

  return (
    <GestureDetector gesture={collapsedGesture}>
      <View style={[
        styles.collapsedNav,
        {
          height: 28 + Math.max(insetsBottom, 6),
          paddingBottom: Math.max(insetsBottom, 6),
        },
      ]}>
        <Pressable onPress={onOpenNav} style={styles.collapsedNavOpener} accessibilityRole="button" accessibilityLabel={showNavigationLabel}>
          <Ionicons name="chevron-up" size={14} color={colors.primary} />
          <View style={styles.collapsedHandle} />
          <Ionicons name={activeItem.activeIcon ?? activeItem.icon} size={14} color={colors.primary} />
        </Pressable>
        {showCollapsedCurrentSlot ? (
          <View style={styles.collapsedCurrentSlot}>
            {showTodayShortcut ? (
              <Pressable onPress={onCalendarToday} style={styles.collapsedCurrentPill} accessibilityRole="button" accessibilityLabel={todayLabel}>
                <Ionicons name="today-outline" size={12} color={colors.tabBarActive} />
                <Text style={styles.collapsedCurrentText}>{todayLabel}</Text>
              </Pressable>
            ) : (
              <View style={styles.collapsedCurrentPill} accessible accessibilityLabel={activeItem.label}>
                <Ionicons name={activeItem.activeIcon ?? activeItem.icon} size={12} color={colors.tabBarActive} />
                {collapsedLabel ? <Text style={styles.collapsedCurrentText}>{collapsedLabel}</Text> : null}
                {collapsedBadge ? (
                  <View style={[styles.collapsedBadge, { backgroundColor: activeItem.badgeColor ?? colors.primary }]}>
                    <Text style={styles.collapsedBadgeText}>{collapsedBadge}</Text>
                  </View>
                ) : null}
              </View>
            )}
          </View>
        ) : null}
      </View>
    </GestureDetector>
  );
}

export default function TabsLayout() {
  const { t } = useTranslation();
  const insets = useSafeAreaInsets();
  const router = useRouter();
  const segments = useSegments();
  const { colors, isDark } = useTheme();
  const queueCalendarFocus = useNavigationStore((state) => state.queueCalendarFocus);
  const navOpen = useNavigationStore((state) => state.tabBarExpanded);
  const setTabBarChrome = useNavigationStore((state) => state.setTabBarChrome);
  const tabBarKeyboardVisible = useNavigationStore((state) => state.tabBarKeyboardVisible);
  const setTabBarKeyboardVisible = useNavigationStore((state) => state.setTabBarKeyboardVisible);
  const globalDayNavigatorOpen = useNavigationStore((state) => state.globalDayNavigatorOpen);
  const setGlobalDayNavigatorOpen = useNavigationStore((state) => state.setGlobalDayNavigatorOpen);
  const reminders = useRemindersStore((state) => state.reminders);
  const overdueCount = useMemo(() => {
    const now = new Date();
    return reminders.filter((reminder) => getReminderStatus(reminder) === 'active' && reminder.run_at_utc && new Date(reminder.run_at_utc) < now).length;
  }, [reminders]);
  const unreadNotifs = useNotificationHistory((state) => state.unreadCount());
  const activeTab = useMemo(() => getActiveTab(segments), [segments]);
  // The compact strip is one 28dp control row plus the device bottom inset.
  // Reserving the old 46dp row left a visible dead band above the navigator.
  const collapsedBarSpace = 28 + Math.max(insets.bottom, 6);
  const expandedBarSpace = 82 + (insets.bottom > 0 ? insets.bottom : 8);
  const currentBarSpace = navOpen ? expandedBarSpace : collapsedBarSpace;
  const hideTabBarForKeyboard = tabBarKeyboardVisible;
  const tabItems = useMemo<TabItem[]>(() => [
    {
      key: 'chat',
      label: t('tabs.chat'),
      route: '/(tabs)/chat',
      icon: 'chatbubble-outline',
      activeIcon: 'chatbubble',
    },
    {
      key: 'reminders',
      label: t('tabs.reminders'),
      route: '/(tabs)/reminders',
      icon: 'notifications-outline',
      activeIcon: 'notifications',
      badge: overdueCount > 0 ? overdueCount : undefined,
      badgeColor: '#FF3B30',
    },
    {
      key: 'calendar',
      label: t('tabs.calendar'),
      route: '/(tabs)/calendar',
      icon: 'calendar-outline',
      activeIcon: 'calendar',
    },
    {
      key: 'settings',
      label: t('tabs.settings'),
      route: '/(tabs)/settings',
      icon: 'settings-outline',
      activeIcon: 'settings',
      badge: unreadNotifs > 0 ? unreadNotifs : undefined,
      badgeColor: colors.primary,
    },
  ], [colors.primary, overdueCount, t, unreadNotifs]);

  const handleNavigate = (route: string) => {
    setGlobalDayNavigatorOpen(false);
    router.replace(route as never);
  };

  const closeGlobalDayNavigator = useCallback(() => {
    setGlobalDayNavigatorOpen(false);
    setTabBarChrome({ expanded: true, bottomSpace: expandedBarSpace });
  }, [expandedBarSpace, setTabBarChrome]);

  const openNav = useCallback(() => {
    setTabBarChrome({ expanded: true, bottomSpace: expandedBarSpace });
  }, [expandedBarSpace, setTabBarChrome]);

  const closeNav = useCallback(() => {
    setTabBarChrome({ expanded: false, bottomSpace: collapsedBarSpace });
  }, [collapsedBarSpace, setTabBarChrome]);

  useEffect(() => {
    setTabBarChrome({ expanded: navOpen, bottomSpace: currentBarSpace });
  }, [currentBarSpace, navOpen, setTabBarChrome]);

  useEffect(() => {
    if (Platform.OS === 'web') {
      if (typeof window === 'undefined' || typeof document === 'undefined') return;

      const viewport = window.visualViewport;
      let baselineHeight = viewport?.height ?? window.innerHeight;

      const hasEditableFocus = () => {
        const activeElement = document.activeElement as HTMLElement | null;
        if (!activeElement) return false;
        const tagName = activeElement.tagName.toLowerCase();
        return tagName === 'input' || tagName === 'textarea' || activeElement.isContentEditable;
      };

      const updateKeyboardState = () => {
        const currentHeight = viewport?.height ?? window.innerHeight;
        if (!hasEditableFocus()) {
          baselineHeight = Math.max(baselineHeight, currentHeight);
          setTabBarKeyboardVisible(false);
          return;
        }

        const keyboardVisible = baselineHeight - currentHeight > 80;
        setTabBarKeyboardVisible(keyboardVisible);
      };

      const handleFocusIn = () => window.setTimeout(updateKeyboardState, 120);
      const handleFocusOut = () => window.setTimeout(updateKeyboardState, 120);
      const handleResize = () => window.requestAnimationFrame(updateKeyboardState);

      document.addEventListener('focusin', handleFocusIn);
      document.addEventListener('focusout', handleFocusOut);
      window.addEventListener('resize', handleResize);
      viewport?.addEventListener('resize', handleResize);
      viewport?.addEventListener('scroll', handleResize);

      return () => {
        document.removeEventListener('focusin', handleFocusIn);
        document.removeEventListener('focusout', handleFocusOut);
        window.removeEventListener('resize', handleResize);
        viewport?.removeEventListener('resize', handleResize);
        viewport?.removeEventListener('scroll', handleResize);
        setTabBarKeyboardVisible(false);
      };
    }

    const showEvent = Platform.OS === 'ios' ? 'keyboardWillShow' : 'keyboardDidShow';
    const hideEvent = Platform.OS === 'ios' ? 'keyboardWillHide' : 'keyboardDidHide';
    const showSub = Keyboard.addListener(showEvent, (event) => {
      // Hardware-keyboard accessory strips report a show event without occupying the bottom viewport.
      const keyboardOccupiesBottom = Platform.OS !== 'android'
        || event.endCoordinates.height > MIN_BOTTOM_KEYBOARD_HEIGHT;
      setTabBarKeyboardVisible(keyboardOccupiesBottom);
    });
    const hideSub = Keyboard.addListener(hideEvent, () => setTabBarKeyboardVisible(false));
    const appStateSub = AppState.addEventListener('change', () => {
      setTabBarKeyboardVisible(false);
    });

    setTabBarKeyboardVisible(false);

    return () => {
      showSub.remove();
      hideSub.remove();
      appStateSub.remove();
      setTabBarKeyboardVisible(false);
    };
  }, [setTabBarKeyboardVisible]);

  const handleCalendarToday = useCallback(() => {
    const dateKey = DateTime.local().toISODate();
    if (!dateKey) return;

    if (activeTab !== 'calendar') {
      useCalendarStore.getState().setSelectedDate(dateKey);
      return;
    }

    queueCalendarFocus({
      dateKey,
      openDayPanel: true,
      animateMonthChange: true,
    });
  }, [activeTab, queueCalendarFocus]);

  const handleOpenCalendarDayPanel = useCallback(() => {
    closeNav();
    if (activeTab !== 'calendar') {
      setGlobalDayNavigatorOpen(true);
      return;
    }

    const dateKey = useCalendarStore.getState().selectedDate || DateTime.local().toISODate();
    if (!dateKey) return;
    queueCalendarFocus({
      dateKey,
      openDayPanel: true,
      animateMonthChange: false,
    });
  }, [activeTab, closeNav, queueCalendarFocus]);

  useEffect(() => {
    if (activeTab === 'calendar') setGlobalDayNavigatorOpen(false);
  }, [activeTab]);

  return (
    <View style={layoutStyles.container}>
      <SupportBanner />
      <View style={layoutStyles.body}>
        <Tabs
          tabBar={() => null}
          screenOptions={{
            headerShown: false,
            animation: 'none',
          }}
        >
          <Tabs.Screen name="chat" />
          <Tabs.Screen name="reminders" />
          <Tabs.Screen name="calendar" />
          <Tabs.Screen name="settings" />
        </Tabs>
      </View>
      <GlobalDayNavigatorOverlay
        visible={globalDayNavigatorOpen}
        bottomSpace={currentBarSpace}
        onClose={closeGlobalDayNavigator}
      />
      {!hideTabBarForKeyboard && (
        <CollapsibleTabBar
          activeTab={activeTab}
          items={tabItems}
          colors={colors}
          isDark={isDark}
          insetsBottom={insets.bottom}
          navOpen={navOpen}
          dayNavigatorOpen={globalDayNavigatorOpen}
          onNavigate={handleNavigate}
          onOpenNav={openNav}
          onCloseNav={closeNav}
          onCalendarToday={handleCalendarToday}
          onOpenCalendarDayPanel={handleOpenCalendarDayPanel}
          showNavigationLabel={t('common.showNavigation', 'Show navigation')}
          hideNavigationLabel={t('common.hideNavigation', 'Hide navigation')}
          todayLabel={t('calendar.today', 'Today')}
        />
      )}
    </View>
  );
}

const layoutStyles = StyleSheet.create({
  container: {
    flex: 1,
  },
  body: {
    flex: 1,
  },
});
